import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { CurvedSlider } from '@/features/charts/components/curved-slider';
import { getCustomOverlayPosition } from '@/features/charts/util/chart-styling-util';
import { Gradients } from '@/shared/components/theme/gradient';
import { KpiUnit } from '@/shared/types/kpi';
import { cn } from '@/shared/utils/cn';
import { formatNumberWithDots } from '@/shared/utils/format';

interface BudgetHoverOverlayProps {
  isVisible: boolean;
  mouseX: number;
  mouseY: number;
  maxValue: number;
  currValue: number;
  unit: string | KpiUnit | null;
}

export const BudgetHoverOverlay = ({
  isVisible,
  mouseX,
  mouseY,
  maxValue,
  currValue,
  unit,
}: BudgetHoverOverlayProps) => {
  const { t } = useTranslation('chart', { keyPrefix: 'tooltip' });
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 150);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) {
    return null;
  }

  const tooltipPosition = getCustomOverlayPosition(mouseX, mouseY, 200, 250);
  const unitString = typeof unit === 'string' ? unit : unit?.code || '';

  const overlayContent = (
    <div
      className="pointer-events-none fixed z-50"
      style={{
        left: tooltipPosition.x,
        top: tooltipPosition.y,
      }}
    >
      <div
        className={cn(
          'rounded-md border-[0.5px] border-white/10 bg-dropdown-gray backdrop-blur-[30px] transition-all duration-150 ease-out',
          isAnimating
            ? 'animate-in fade-in-0 zoom-in-95'
            : 'animate-out fade-out-0 zoom-out-95',
        )}
      >
        <div
          className={cn(
            'flex flex-col border-white/15 bg-gradient-to-br',
            Gradients.dropdownGradient,
          )}
        >
          <div className="w-fit min-w-48">
            <div className="flex flex-row justify-between pl-[14px] pr-2 pt-2">
              <span className="text-2xs">{t('budget')}</span>
            </div>
            <div className="my-2 h-px w-full bg-white/30" />
            <div className="flex flex-col items-center gap-1 p-5">
              <div className="flex flex-col items-center justify-center gap-2">
                <CurvedSlider
                  currValue={currValue}
                  maxValue={maxValue}
                  size={160}
                  strokeWidth={16}
                  showPercentage={true}
                />
                <span className="text-4xs font-bold">{`${formatNumberWithDots(currValue, 2)} ${unitString}`}</span>
                <span className="text-3xs text-custom-medium-grey">{`von Budget: ${formatNumberWithDots(maxValue, 2)} ${unitString}`}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
};
